import { useEffect, useState } from 'react';
import { useInput, usePhoneInput } from '../../../Hooks';
import { winnreAuth } from '../../../utils/axios';
import countries from '../../../data/countries.json';
import statecities from '../../../data/statecities.json';

export const useData = (isInternationalCustomer, loginWithEmail) => {
    const {
        value: name,
        isValid: nameIsValid,
        hasError: nameHasError,
        valueChangeHandler: nameChangedHandler,
        inputBlurHandler: nameBlurHandler,
        reset: resetName
    } = useInput((value) => value.trim() !== '');

    const {
        value: companyName,
        isValid: companyNameIsValid,
        hasError: companyNameHasError,
        valueChangeHandler: companyNameChangedHandler,
        inputBlurHandler: companyNameBlurHandler,
        reset: resetCompanyName
    } = useInput((value) => value.trim() !== '');

    const [companyTypes, setCompanyTypes] = useState([]);
    const [companyType, setCompanyType] = useState('COMPANY');
    const [projectList, setProjectList] = useState([]);
    const [unitValidated, setUnitValidated] = useState(false);
    const [project, setProject] = useState(null); //selected

    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);

    const {
        value: unitNumber,
        isValid: unitNumberIsValid,
        hasError: unitNumberHasError,
        valueChangeHandler: unitNumberChangedHandler,
        inputBlurHandler: unitNumberBlurHandler,
        reset: resetUnitNumber
    } = useInput((value) => value !== '');

    const {
        value: addressLine1,
        isValid: addressLine1IsValid,
        hasError: addressLine1HasError,
        valueChangeHandler: addressLine1ChangedHandler,
        inputBlurHandler: addressLine1BlurHandler,
        reset: resetAddressLine1
    } = useInput((value) => value.trim() !== '');

    const {
        value: addressLine2,
        valueChangeHandler: addressLine2ChangedHandler,
        inputBlurHandler: addressLine2BlurHandler,
        reset: resetAddressLine2
    } = useInput(() => true);

    const {
        value: city,
        valueChangeHandler: cityChangedHandler,
        inputBlurHandler: cityBlurHandler,
        reset: resetCity
    } = useInput((value) => value.trim() !== '');

    const {
        value: stateVal, // 'state' is a reserved keyword in React
        isValid: stateIsValid,
        hasError: stateHasError,
        valueChangeHandler: stateChangedHandler,
        inputBlurHandler: stateBlurHandler,
        reset: resetState
    } = useInput((value) => value.trim() !== '');

    useEffect(() => {
        setCountryList(countries);
        const mappedStates = statecities.States.map((s) => ({
            name: s.StateName,
            code: s.StateName
        }));
        setStateList(mappedStates);
        setCityList([]);
    }, []);

    useEffect(() => {
        if (stateVal && !isInternationalCustomer && !loginWithEmail) {
            const selectedState = statecities.States.find(
                (s) => s.StateName === stateVal
            );
            if (selectedState) {
                const mappedCities = selectedState.Cities.map((cityName) => ({
                    name: cityName,
                    stateCode: stateVal
                }));
                setCityList(mappedCities);
            } else {
                setCityList([]);
            }
            // Reset city when state changes to avoid invalid city-state combinations
            resetCity();
        } else {
            setCityList([]);
        }
    }, [stateVal, isInternationalCustomer, loginWithEmail]);

    const {
        value: pincode,
        isValid: pincodeIsValid,
        hasError: pincodeHasError,
        valueChangeHandler: pincodeChangedHandler,
        inputBlurHandler: pincodeBlurHandler,
        reset: resetPincode
    } = useInput((value) => value.length === 6);

    const {
        value: country,
        isValid: countryIsValid,
        hasError: countryHasError,
        valueChangeHandler: countryChangedHandler,
        inputBlurHandler: countryBlurHandler,
        reset: resetCountry
    } = useInput((value) => value.trim() !== '');

    useEffect(() => {
        if (!isInternationalCustomer) {
            countryChangedHandler({ target: { value: 'India' } });
        }
    }, [isInternationalCustomer, countryChangedHandler]);

    const {
        value: panNumber,
        isValid: panNumberIsValid,
        hasError: panNumberHasError,
        valueChangeHandler: panNumberChangedHandler,
        inputBlurHandler: panNumberBlurHandler,
        reset: resetpanNumber
    } = useInput((value) => {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        return panRegex.test(value.toUpperCase());
    });

    const {
        value: reraNumber,
        isValid: reraNumberIsValid,
        hasError: reraNumberHasError,
        valueChangeHandler: reraNumberChangedHandler,
        inputBlurHandler: reraNumberBlurHandler,
        reset: resetReraNumber
    } = useInput((value) => {
        // RERA format validation - basic check for alphanumeric characters
        // Actual RERA format varies by state, so keeping it flexible
        if (!value) return true; // Optional field
        const reraRegex = /^[A-Z0-9]{5,20}$/;
        return reraRegex.test(value.toUpperCase());
    });

    const {
        state: {
            isValid: currentMobileNumberIsValid,
            mobile: currentMobile,
            country: currentCountry,
            mobileHasError: currentMobileHasError,
            isInputFocused: isCurrentMobileInputFocused
        },
        onChange: currentMobileOnChange,
        validator: currentMobileValidtor,
        setIsTouched: setCurrentMobileIsTouched,
        getMobileWithoutCountryCode: getCurrentMobileWithoutCountryCode,
        setInputFocused: setCurrentMobileInputFocused
    } = usePhoneInput();

    const getCompanyTypes = () => {
        winnreAuth({
            method: 'GET',
            url: 'winnre/no-auth/enum/cp-types'
        })
            .then((res) => {
                setCompanyTypes(
                    Object.entries(res).map(([value, label]) => ({
                        value,
                        label
                    }))
                );
            })
            .catch(() => {});
    };

    useEffect(() => {
        getCompanyTypes();
    }, []);

    const getProjects = () => {
        winnreAuth({
            method: 'GET',
            url: `winnre/user/project/all-names`
        })
            .then((res) => {
                setProjectList(
                    res.map((obj) => {
                        return {
                            id: obj.id,
                            projectName: obj.projectName
                        };
                    })
                );
            })
            .catch(() => {});
    };

    return {
        state: {
            name,
            nameIsValid,
            nameHasError,
            unitNumber,
            unitNumberIsValid,
            unitNumberHasError,
            currentMobile,
            currentCountry,
            currentMobileHasError,
            isCurrentMobileInputFocused,
            project,
            projectList,
            unitValidated,
            currentMobileNumberIsValid,
            companyName,
            companyNameIsValid,
            companyNameHasError,
            companyTypes,
            companyType,
            addressLine1,
            addressLine1IsValid,
            addressLine1HasError,
            addressLine2,
            city,
            cityList,
            stateVal,
            stateList,
            stateIsValid,
            stateHasError,
            pincode,
            pincodeIsValid,
            pincodeHasError,
            country,
            countryList,
            countryIsValid,
            countryHasError,
            panNumber,
            panNumberIsValid,
            panNumberHasError,
            reraNumber,
            reraNumberIsValid,
            reraNumberHasError
        },
        resetName,
        resetUnitNumber,
        nameChangedHandler,
        nameBlurHandler,
        unitNumberChangedHandler,
        unitNumberBlurHandler,
        getCurrentMobileWithoutCountryCode,
        setCurrentMobileIsTouched,
        setCurrentMobileInputFocused,
        currentMobileOnChange,
        currentMobileValidtor,
        setProject,
        setProjectList,
        setUnitValidated,
        getProjects,
        companyNameChangedHandler,
        companyNameBlurHandler,
        resetCompanyName,
        setCompanyType,
        addressLine1ChangedHandler,
        addressLine1BlurHandler,
        resetAddressLine1,
        addressLine2ChangedHandler,
        addressLine2BlurHandler,
        resetAddressLine2,
        cityChangedHandler,
        cityBlurHandler,
        resetCity,
        stateChangedHandler,
        stateBlurHandler,
        resetState,
        pincodeChangedHandler,
        pincodeBlurHandler,
        resetPincode,
        countryChangedHandler,
        countryBlurHandler,
        resetCountry,
        panNumberChangedHandler,
        panNumberBlurHandler,
        resetpanNumber,
        reraNumberChangedHandler,
        reraNumberBlurHandler,
        resetReraNumber
    };
};
